import React, {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { restaurantMatchesSearch } from '../lib/restaurantSearch';
import '../styles/live-outlet-picker.css';
import { Icon } from './shared/Icon';

export type LiveOutletPickerOption = {
  id: string;
  name: string;
  closed?: boolean;
  city?: string;
  region?: string;
};

function optionSubtitle(o: LiveOutletPickerOption, closedLabel: string): string | null {
  const bits = [o.city, o.region].filter(Boolean);
  if (o.closed) bits.push(closedLabel);
  return bits.length ? bits.join(' · ') : null;
}

export function LiveOutletPicker({
  outlets,
  value,
  onChange,
  label,
  disabled = false,
}: {
  outlets: LiveOutletPickerOption[];
  value: string | null;
  onChange: (id: string) => void;
  label?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation('live');
  const resolvedLabel = label ?? t('picker.restaurant');
  const listId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = outlets.find(o => o.id === value) ?? null;
  const filtered = useMemo(
    () =>
      outlets.filter(o =>
        restaurantMatchesSearch(
          {
            id: o.id,
            name: o.name,
            city: o.city,
            region: o.region,
            label: o.closed ? 'closed' : null,
          },
          query,
        ),
      ),
    [outlets, query],
  );

  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setQuery('');
      const selectedIdx = Math.max(
        0,
        outlets.findIndex(o => o.id === value),
      );
      setActiveIndex(selectedIdx);
      const t = window.setTimeout(() => searchRef.current?.focus(), 0);
      wasOpen.current = true;
      return () => window.clearTimeout(t);
    }
    if (!open) wasOpen.current = false;
  }, [open, outlets, value]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(i =>
      filtered.length === 0 ? 0 : Math.min(i, filtered.length - 1),
    );
  }, [filtered.length, open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-outlet-idx="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const pick = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!filtered.length) return;
      setActiveIndex(i => (i + 1) % filtered.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!filtered.length) return;
      setActiveIndex(i => (i - 1 + filtered.length) % filtered.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const hit = filtered[activeIndex];
      if (hit) pick(hit.id);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      if (filtered.length) setActiveIndex(filtered.length - 1);
    }
  };

  const triggerLabel = selected?.name ?? t('picker.select');
  const triggerSub = selected ? optionSubtitle(selected, t('fleet.closed')) : null;

  return (
    <div
      className={`rib-outlet-picker${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
      ref={rootRef}
    >
      <span className="rib-outlet-filter-lab" id={`${listId}-lab`}>
        {resolvedLabel}
      </span>
      <button
        type="button"
        className="rib-outlet-picker-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${listId}-list`}
        aria-labelledby={`${listId}-lab`}
        disabled={disabled || outlets.length === 0}
        onClick={() => setOpen(v => !v)}
      >
        <span className="rib-outlet-picker-trigger-main">
          <span className="rib-outlet-picker-trigger-name">{triggerLabel}</span>
          {triggerSub && (
            <span className="rib-outlet-picker-trigger-sub">{triggerSub}</span>
          )}
        </span>
        <Icon
          name="chevron"
          size={14}
          stroke={2.2}
          className="rib-outlet-picker-caret"
        />
      </button>

      {open && (
        <div className="rib-outlet-picker-menu" role="presentation">
          <div className="rib-outlet-picker-search">
            <Icon name="search" size={13} className="rib-outlet-picker-search-ic" />
            <input
              ref={searchRef}
              type="search"
              className="rib-outlet-picker-search-input"
              placeholder={t('picker.search')}
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onSearchKeyDown}
              aria-autocomplete="list"
              aria-controls={`${listId}-list`}
              aria-activedescendant={
                filtered[activeIndex]
                  ? `${listId}-opt-${filtered[activeIndex].id}`
                  : undefined
              }
            />
            {query ? (
              <button
                type="button"
                className="rib-outlet-picker-clear"
                aria-label={t('picker.clear')}
                onClick={() => {
                  setQuery('');
                  setActiveIndex(0);
                  searchRef.current?.focus();
                }}
              >
                <Icon name="x" size={12} stroke={2.4} />
              </button>
            ) : null}
          </div>
          <div
            ref={listRef}
            id={`${listId}-list`}
            className="rib-outlet-picker-list"
            role="listbox"
            aria-label={t('picker.fleet')}
          >
            {filtered.length === 0 ? (
              <div className="rib-outlet-picker-empty">{t('picker.noMatch')}</div>
            ) : (
              filtered.map((o, idx) => {
                const sub = optionSubtitle(o, t('fleet.closed'));
                const on = o.id === value;
                return (
                  <button
                    key={o.id}
                    type="button"
                    id={`${listId}-opt-${o.id}`}
                    data-outlet-idx={idx}
                    role="option"
                    aria-selected={on}
                    className={`rib-outlet-picker-opt${on ? ' on' : ''}${
                      idx === activeIndex ? ' is-active' : ''
                    }${o.closed ? ' is-closed' : ''}`}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => pick(o.id)}
                  >
                    <span className="rib-outlet-picker-opt-main">
                      <b>{o.name}</b>
                      {sub && <span>{sub}</span>}
                    </span>
                    {on && (
                      <Icon
                        name="check"
                        size={14}
                        stroke={2.4}
                        className="rib-outlet-picker-check"
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
          <div className="rib-outlet-picker-foot">
            {query.trim()
              ? `${filtered.length} of ${outlets.length}`
              : `${outlets.length} in fleet`}
          </div>
        </div>
      )}
    </div>
  );
}
